import type { ZodObject, ZodRawShape } from 'zod';
import type { DetailFieldParameter } from '@/common/types/detailFieldMeta.types';

type TransformFn = (data: Record<string, unknown>, ctx: object) => Record<string, unknown>;

type ZodPipelineWithTransformDef = {
  in: {
    _def: {
      schema: ZodObject<ZodRawShape>;
      effect: { transform: TransformFn };
    };
  };
  out: ZodObject<ZodRawShape>;
};

type ZodSchemaNode = { shape?: ZodRawShape; _def?: Record<string, unknown> };

const getRequiredMapFromZodObject = (shape: ZodRawShape): Record<string, boolean> => {
  return Object.fromEntries(
    Object.entries(shape).map(([key, field]) => [
      key,
      !(field as { isOptional(): boolean }).isOptional(),
    ]),
  );
};

/**
 * z.object({}).transform().pipe() 같은 형태에서 input 필드별 required 여부를 판별한다.
 *
 * - in._def.schema : transform 이전 원본 ZodObject (input 필드 목록 추출용)
 * - in._def.effect : transform 함수
 * - out            : output ZodObject
 *
 * 빈 값으로 세팅된 baseline과 특정 input 필드에만 sentinel 값(__detect__)을 주입한 결과를 비교해
 * 영향 받은 output 필드를 탐지하고, 그 중 required인 것이 있으면 해당 input 필드를 required로 판별한다.
 */
const getRequiredMapFromZodPipeline = (
  def: ZodPipelineWithTransformDef,
): Record<string, boolean> => {
  const inputSchema = def.in;
  // transform 이전 원본 ZodObject의 input 필드 목록
  const inputFieldNames = Object.keys(inputSchema._def.schema.shape);

  const outSchema = def.out as unknown as {
    shape?: ZodRawShape;
    _def: { schema: ZodObject<ZodRawShape> };
  };
  // output이 .refine()을 포함하면 ZodEffects로 감싸져 .shape가 없으므로 내부 ZodObject의 shape로 unwrap
  const outputShape = outSchema.shape ?? outSchema._def?.schema?.shape ?? {};
  const outputFieldNames = Object.keys(outputShape);

  const mockZodCtx = { addIssue: () => {}, path: [] };

  // 런타임 에러가 발생할 수 있어, fallback으로 input shape 기준 판별
  try {
    const transformEffect = def.in._def.effect.transform;
    // 빈 값으로 세팅된 input 기준 transform 결과 (sentinel 비교 기준)
    const baselineOutput = transformEffect({}, mockZodCtx);

    return Object.fromEntries(
      inputFieldNames.map((inputField) => {
        // input 필드에만 sentinel 값을 주입한 transform 결과
        const sentinelOutput = transformEffect({ [inputField]: '__detect__' }, mockZodCtx);
        // baseline 대비 값이 달라진 output 필드
        const affectedOutputFields = outputFieldNames.filter(
          (outputField) => sentinelOutput[outputField] !== baselineOutput[outputField],
        );
        const isRequired =
          affectedOutputFields.length > 0
            ? // rename, merge된 경우: 영향 받은 output 필드 중 하나라도 required이면 true
              affectedOutputFields.some((outputField) => !outputShape[outputField]?.isOptional())
            : // 이름이 그대로 유지된 경우: output의 동일 이름 필드 기준으로 판별
              outputShape[inputField] !== undefined && !outputShape[inputField]?.isOptional();
        return [inputField, isRequired];
      }),
    );
  } catch {
    return getRequiredMapFromZodObject(inputSchema._def.schema.shape);
  }
};

export const getRequiredMap = (schema: unknown): Record<string, boolean> => {
  const s = schema as ZodSchemaNode;

  if (s?.shape) {
    return getRequiredMapFromZodObject(s.shape);
  }

  const def = s?._def;

  if (def?.in && def?.out) {
    return getRequiredMapFromZodPipeline(def as unknown as ZodPipelineWithTransformDef);
  }

  if (def?.schema) {
    return getRequiredMap(def.schema);
  }

  return {};
};

interface DetailTargetInfo {
  targetId: string | undefined;
  targetName: string | undefined;
  addition: Record<string, string | number | boolean | undefined>;
}

export const extractDetailTargetInfo = (
  parameterDefines: DetailFieldParameter[],
  mappedRowData: Record<string, any>,
): DetailTargetInfo => {
  const targetIdField = parameterDefines.find((p) => p.dataAttribute === 'TargetId')?.field || '';
  const targetNameField =
    parameterDefines.find((p) => p.dataAttribute === 'TargetName')?.field || '';

  const addition = parameterDefines.reduce<Record<string, string | number | boolean | undefined>>(
    (acc, param) => {
      if (param.detailParameterType === 'addition') {
        acc[param.field] = mappedRowData[param.field];
      }
      return acc;
    },
    {},
  );

  return {
    targetId: mappedRowData[targetIdField],
    targetName: mappedRowData[targetNameField],
    addition,
  };
};
